interface useConfettiProps {
  size: ScreenSize
}

export type ScreenSize = 'small' | 'medium' | 'large'

interface ConfettiStyles {
  position: 'absolute'
  height: string
  width: string
  top?: string
  left?: string
  right?: string
  bottom?: string
}

interface Confetti {
  key: string
  icon: string
  styles: ConfettiStyles
}

export const useConfetti = ({ size }: useConfettiProps) => {
  switch (size) {
    case 'small':
      return iconsForSmallScreen
    case 'medium':
      return iconsForMediumScreen
    case 'large':
    default:
      return iconsForLargeScreen
  }
}

const iconSizes = {
  circle: {
    height: '12px',
    width: '12px',
  },
  arch: {
    height: '15px',
    width: '9px',
  },
  rainbow: {
    height: '11px',
    width: '20px',
  },
  triangle: {
    height: '11px',
    width: '13px',
  },
}

const iconUrls = {
  triangle: {
    yellowToOrange:
      'https://images.babylist.com/image/upload/v1684187216/Dynamic%20Landing%20Pages/Confetti/Confetti_triangle_yellow-to-orange.svg',
    lemonLime:
      'https://images.babylist.com/image/upload/v1684187215/Dynamic%20Landing%20Pages/Confetti/Confetti_triangle_lemon-lime.svg',
    sunset:
      'https://images.babylist.com/image/upload/v1684187215/Dynamic%20Landing%20Pages/Confetti/Confetti_triangle_sunset.svg',
    nighttime:
      'https://images.babylist.com/image/upload/v1684187215/Dynamic%20Landing%20Pages/Confetti/Confetti_triangle_nighttime.svg',
  },
  rainbow: {
    sunset:
      'https://images.babylist.com/image/upload/v1684187215/Dynamic%20Landing%20Pages/Confetti/Confetti_rainbow_sunset.svg',
    strawberry:
      'https://images.babylist.com/image/upload/v1684187214/Dynamic%20Landing%20Pages/Confetti/Confetti_rainbow_strawberry.svg',
    lemonLime:
      'https://images.babylist.com/image/upload/v1684187214/Dynamic%20Landing%20Pages/Confetti/Confetti_rainbow_lemon-lime.svg',
    purpleToBlue:
      'https://images.babylist.com/image/upload/v1684187214/Dynamic%20Landing%20Pages/Confetti/Confetti_rainbow_purple-to-blue.svg',
  },
  circle: {
    turquoise:
      'https://images.babylist.com/image/upload/v1684187214/Dynamic%20Landing%20Pages/Confetti/Confetti_circle_turquoise.svg',
    peachToPink:
      'https://images.babylist.com/image/upload/v1684187214/Dynamic%20Landing%20Pages/Confetti/Confetti_circle_peach-to-pink.svg',
    nighttime:
      'https://images.babylist.com/image/upload/v1684187212/Dynamic%20Landing%20Pages/Confetti/Confetti_circle_nighttime.svg',
  },
  arch: {
    nighttime:
      'https://images.babylist.com/image/upload/v1684187212/Dynamic%20Landing%20Pages/Confetti/Confetti_arch_nighttime.svg',
    turquoise:
      'https://images.babylist.com/image/upload/v1684187212/Dynamic%20Landing%20Pages/Confetti/Confetti_arch_turquoise.svg',
    sunset:
      'https://images.babylist.com/image/upload/v1684187212/Dynamic%20Landing%20Pages/Confetti/Confetti_arch_sunset.svg',
    greenToYellow:
      'https://images.babylist.com/image/upload/v1684187212/Dynamic%20Landing%20Pages/Confetti/Confetti_arch_green-to-yellow.svg',
  },
}

const iconsForSmallScreen: Confetti[] = [
  {
    key: 'confetti-small-1',
    icon: iconUrls.rainbow.sunset,
    styles: {
      position: 'absolute',
      top: '10px',
      left: '178px',
      ...iconSizes.rainbow,
    },
  },
  {
    key: 'confetti-small-2',
    icon: iconUrls.rainbow.purpleToBlue,
    styles: {
      position: 'absolute',
      top: '87px',
      left: '11.5px',
      ...iconSizes.rainbow,
    },
  },
  {
    key: 'confetti-small-3',
    icon: iconUrls.arch.turquoise,
    styles: {
      position: 'absolute',
      top: '138px',
      right: '17px',
      ...iconSizes.arch,
    },
  },
  {
    key: 'confetti-small-4',
    icon: iconUrls.circle.peachToPink,
    styles: {
      position: 'absolute',
      bottom: '127px',
      left: '15px',
      ...iconSizes.circle,
    },
  },
  {
    key: 'confetti-small-5',
    icon: iconUrls.circle.nighttime,
    styles: {
      position: 'absolute',
      bottom: '22.81px',
      left: '154.5px',
      ...iconSizes.circle,
    },
  },
  {
    key: 'confetti-small-6',
    icon: iconUrls.triangle.sunset,
    styles: {
      position: 'absolute',
      bottom: '12.91px',
      right: '54.08',
      ...iconSizes.triangle,
    },
  },
]

const iconsForMediumScreen: Confetti[] = [
  {
    key: 'confetti-medium-1',
    icon: iconUrls.triangle.yellowToOrange,
    styles: {
      position: 'absolute',
      top: '21px',
      left: '23.04px',
      ...iconSizes.triangle,
    },
  },
  {
    key: 'confetti-medium-2',
    icon: iconUrls.rainbow.purpleToBlue,
    styles: {
      position: 'absolute',
      top: '28px',
      left: '360px',
      ...iconSizes.rainbow,
    },
  },
  {
    key: 'confetti-medium-3',
    icon: iconUrls.arch.greenToYellow,
    styles: {
      position: 'absolute',
      top: '12px',
      right: '78px',
      ...iconSizes.arch,
    },
  },
  {
    key: 'confetti-medium-4',
    icon: iconUrls.circle.peachToPink,
    styles: {
      position: 'absolute',
      top: '119.19px',
      right: '16.5px',
      ...iconSizes.circle,
    },
  },
  {
    key: 'confetti-medium-5',
    icon: iconUrls.circle.peachToPink,
    styles: {
      position: 'absolute',
      top: '244px',
      left: '15px',
      ...iconSizes.circle,
    },
  },
  {
    key: 'confetti-medium-6',
    icon: iconUrls.circle.turquoise,
    styles: {
      position: 'absolute',
      bottom: '124.81px',
      left: '131.5px',
      ...iconSizes.circle,
    },
  },
  {
    key: 'confetti-medium-7',
    icon: iconUrls.arch.greenToYellow,
    styles: {
      position: 'absolute',
      bottom: '12px',
      left: '59px',
      ...iconSizes.arch,
    },
  },
  {
    key: 'confetti-medium-8',
    icon: iconUrls.circle.peachToPink,
    styles: {
      position: 'absolute',
      bottom: '16px',
      right: '334px',
      ...iconSizes.circle,
    },
  },
  {
    key: 'confetti-medium-9',
    icon: iconUrls.triangle.nighttime,
    styles: {
      position: 'absolute',
      bottom: '97.91px',
      right: '156.04px',
      ...iconSizes.triangle,
    },
  },
  {
    key: 'confetti-medium-10',
    icon: iconUrls.arch.greenToYellow,
    styles: {
      position: 'absolute',
      bottom: '159px',
      right: '48px',
      ...iconSizes.arch,
    },
  },
  {
    key: 'confetti-medium-11',
    icon: iconUrls.rainbow.sunset,
    styles: {
      position: 'absolute',
      bottom: '25px',
      right: '22px',
      ...iconSizes.rainbow,
    },
  },
]

const iconsForLargeScreen: Confetti[] = [
  {
    key: 'confetti-large-1',
    icon: iconUrls.circle.peachToPink,
    styles: {
      position: 'absolute',
      top: '19.19px',
      left: '391.5px',
      ...iconSizes.circle,
    },
  },
  {
    key: 'confetti-large-2',
    icon: iconUrls.rainbow.sunset,
    styles: {
      position: 'absolute',
      top: '49px',
      left: '177.5px',
      ...iconSizes.rainbow,
    },
  },
  {
    key: 'confetti-large-3',
    icon: iconUrls.arch.nighttime,
    styles: {
      position: 'absolute',
      top: '104px',
      left: '37px',
      ...iconSizes.arch,
    },
  },
  {
    key: 'confetti-large-4',
    icon: iconUrls.circle.peachToPink,
    styles: {
      position: 'absolute',
      top: '216px',
      left: '145px',
      ...iconSizes.circle,
    },
  },
  {
    key: 'confetti-large-5',
    icon: iconUrls.triangle.yellowToOrange,
    styles: {
      position: 'absolute',
      bottom: '137.91px',
      left: '51.04px',
      ...iconSizes.triangle,
    },
  },
  {
    key: 'confetti-large-6',
    icon: iconUrls.arch.turquoise,
    styles: {
      position: 'absolute',
      bottom: '51px',
      left: '163px',
      ...iconSizes.arch,
    },
  },
  {
    key: 'confetti-large-7',
    icon: iconUrls.triangle.nighttime,
    styles: {
      position: 'absolute',
      bottom: '157.91px',
      left: '281.04px',
      ...iconSizes.triangle,
    },
  },
  {
    key: 'confetti-large-8',
    icon: iconUrls.rainbow.strawberry,
    styles: {
      position: 'absolute',
      bottom: '28px',
      left: '392px',
      ...iconSizes.rainbow,
    },
  },
  {
    key: 'confetti-large-9',
    icon: iconUrls.triangle.lemonLime,
    styles: {
      position: 'absolute',
      bottom: '27.91px',
      right: '516.04px',
      ...iconSizes.triangle,
    },
  },
  {
    key: 'confetti-large-10',
    icon: iconUrls.arch.sunset,
    styles: {
      position: 'absolute',
      bottom: '129px',
      right: '328px',
      ...iconSizes.arch,
    },
  },
  {
    key: 'confetti-large-11',
    icon: iconUrls.circle.peachToPink,
    styles: {
      position: 'absolute',
      bottom: '32.81px',
      right: '256.5px',
      ...iconSizes.circle,
    },
  },
  {
    key: 'confetti-large-12',
    icon: iconUrls.rainbow.lemonLime,
    styles: {
      position: 'absolute',
      right: '22px',
      bottom: '25px',
      ...iconSizes.rainbow,
    },
  },
  {
    key: 'confetti-large-13',
    icon: iconUrls.triangle.nighttime,
    styles: {
      position: 'absolute',
      bottom: '147.91px',
      right: '126.04px',
      ...iconSizes.triangle,
    },
  },
  {
    key: 'confetti-large-14',
    icon: iconUrls.arch.turquoise,
    styles: {
      position: 'absolute',
      top: '210px',
      right: '48px',
      ...iconSizes.arch,
    },
  },
  {
    key: 'confetti-large-15',
    icon: iconUrls.rainbow.sunset,
    styles: {
      position: 'absolute',
      top: '118px',
      right: '172px',
      ...iconSizes.rainbow,
    },
  },
  {
    key: 'confetti-large-16',
    icon: iconUrls.arch.greenToYellow,
    styles: {
      position: 'absolute',
      top: '22px',
      right: '88px',
      ...iconSizes.arch,
    },
  },
  {
    key: 'confetti-large-17',
    icon: iconUrls.triangle.nighttime,
    styles: {
      position: 'absolute',
      top: '4.09px',
      right: '317.04px',
      ...iconSizes.triangle,
    },
  },
]
